import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const CUTOFF = new Date("2025-02-01T00:00:00");

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const flattenDoc = (docSnap) => {
    const d = docSnap.data();
    const flat = { _doc_id: docSnap.id };
    Object.entries(d).forEach(([k, v]) => {
        if (v && typeof v.toDate === "function") flat[k] = v.toDate().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        else if (v instanceof Date) flat[k] = v.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        else if (typeof v === "object" && v !== null) flat[k] = JSON.stringify(v);
        else flat[k] = v;
    });
    return flat;
};

const downloadExcel = async (collName, fileName, setLoading, filter) => {
    setLoading(true);
    try {
        const ref = collection(db, collName);
        const q = filter ? query(ref, where(filter.field, "==", filter.value)) : ref;
        const snap = await getDocs(q);
        if (snap.empty) { alert("No data found in " + collName); return; }
        const rows = snap.docs.map(flattenDoc);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), collName.slice(0, 31));
        XLSX.writeFile(wb, fileName);
    } catch (err) {
        console.error(err);
        alert("Error fetching " + collName + ": " + err.message);
    } finally {
        setLoading(false);
    }
};

const DATASETS = [
    {
        key: "appointments",
        title: "Appointments Data",
        coll: "appointments_data",
        file: "appointments_data.xlsx",
        color: "#4361ee",
        icon: "📅",
        desc: "All records from appointments_data collection",
        showCounts: true,
    },
    {
        key: "bookings",
        title: "NV Create Booking",
        coll: "nv_create_booking",
        file: "nv_create_booking.xlsx",
        color: "#0d9488",
        icon: "📋",
        desc: "All records from nv_create_booking collection",
        showCounts: true,
    },
    {
        key: "income",
        title: "NV Income",
        coll: "nv_income",
        file: "nv_income.xlsx",
        color: "#16a34a",
        icon: "💰",
        desc: "All records from nv_income collection",
        showCounts: true,
    },
    {
        key: "expense",
        title: "NV Expense",
        coll: "nv_expense",
        file: "nv_expense.xlsx",
        color: "#dc2626",
        icon: "💸",
        desc: "All records from nv_expense collection",
        showCounts: true,
    },
    {
        key: "banner_request",
        title: "Banner Request",
        coll: "banner_request",
        file: "banner_request.xlsx",
        color: "#f59e0b",
        icon: "🖼️",
        desc: "All records from banner_request collection",
        showCounts: true,
    },
    {
        key: "business_card_request",
        title: "Business Card Request",
        coll: "business_card_request",
        file: "business_card_request.xlsx",
        color: "#8b5cf6",
        icon: "🪪",
        desc: "All records from business_card_request collection",
        showCounts: true,
    },
    {
        key: "google_business_request",
        title: "Google Business Request",
        coll: "google_business_request",
        file: "google_business_request.xlsx",
        color: "#0ea5e9",
        icon: "🔍",
        desc: "All records from google_business_request collection",
        showCounts: true,
    },
    {
        key: "premium_banner_request",
        title: "Premium Banner Request",
        coll: "premium_banner_request",
        file: "premium_banner_request.xlsx",
        color: "#d97706",
        icon: "⭐",
        desc: "payment_status = Payment Successful",
        filter: { field: "payment_status", value: "Payment Successful" },
        showCounts: true,
    },
    {
        key: "premium_card_request",
        title: "Premium Card Request",
        coll: "premium_card_request",
        file: "premium_card_request.xlsx",
        color: "#7c3aed",
        icon: "⭐",
        desc: "payment_status = Payment Successful",
        filter: { field: "payment_status", value: "Payment Successful" },
        showCounts: true,
    },
    {
        key: "visiting_cards_created",
        title: "Template Created",
        coll: "visiting_cards_created",
        file: "visiting_cards_created.xlsx",
        color: "#475569",
        icon: "📇",
        desc: "All records from visiting_cards_created collection",
        showCounts: true,
        allAsBefore: true,
    },
    {
        key: "nv_users_saved_draft",
        title: "2.0 Users Card Draft",
        coll: "nv_users_saved_draft",
        file: "nv_users_saved_draft.xlsx",
        color: "#0891b2",
        icon: "📝",
        desc: "All records from nv_users_saved_draft collection",
        showCounts: true,
        dateField: "createdAt",
    },
];

export default function DownloadData() {
    const [loading, setLoading] = useState({});
    const [counts, setCounts] = useState({});

    const setOne = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

    useEffect(() => {
        const fetchCounts = async () => {
            const targets = DATASETS.filter(d => d.showCounts);
            await Promise.all(targets.map(async ({ key, coll, allAsBefore, dateField }) => {
                try {
                    const snap = await getDocs(collection(db, coll));
                    let before = 0, after = 0, unknown = 0;
                    snap.forEach(docSnap => {
                        if (allAsBefore) { before++; return; }
                        const d = docSnap.data();
                        const raw = dateField
                            ? d[dateField]
                            : (d.created_date || d.date || d.appointment_date || d.booking_date);
                        const dt = parseDate(raw);
                        if (!dt) { unknown++; return; }
                        if (dt < CUTOFF) before++; else after++;
                    });
                    setCounts(prev => ({ ...prev, [key]: { before, after, unknown } }));
                } catch (e) {
                    console.error("Count fetch failed for", coll, e);
                }
            }));
        };
        fetchCounts();
    }, []);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">Download Data</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        Fetch full collection and export as Excel
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            <div className="row g-3">
                {DATASETS.map(({ key, title, coll, file, color, icon, desc, filter, showCounts }) => (
                    <div key={key} className="col-md-6">
                        <div
                            className="card border-0 shadow-sm h-100"
                            style={{ borderRadius: "14px", overflow: "hidden" }}
                        >
                            <div
                                className="d-flex align-items-center justify-content-between px-4 py-3 h-100"
                                style={{ borderLeft: `5px solid ${color}` }}
                            >
                                <div>
                                    <div style={{ fontSize: "16px", fontWeight: 700 }}>
                                        {icon}&nbsp;{title}
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                                        {desc}
                                    </div>
                                    {showCounts && (
                                        <div style={{ fontSize: "12px", marginTop: "5px", display: "flex", gap: "14px", flexWrap: "wrap" }}>
                                            <span>
                                                <span style={{ color: "#64748b", fontWeight: 600 }}>Before 1 Feb 2025: </span>
                                                <span style={{ color, fontWeight: 700 }}>
                                                    {counts[key] ? counts[key].before.toLocaleString() : "…"}
                                                </span>
                                            </span>
                                            <span>
                                                <span style={{ color: "#64748b", fontWeight: 600 }}>After 1 Feb 2025: </span>
                                                <span style={{ color, fontWeight: 700 }}>
                                                    {counts[key] ? counts[key].after.toLocaleString() : "…"}
                                                </span>
                                            </span>
                                            {counts[key]?.unknown > 0 && (
                                                <span style={{ color: "#94a3b8" }}>
                                                    (no date: {counts[key].unknown})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => downloadExcel(coll, file, (v) => setOne(key, v), filter)}
                                    disabled={!!loading[key]}
                                    style={{
                                        background: loading[key] ? "#e2e8f0" : color,
                                        color: loading[key] ? "#94a3b8" : "#fff",
                                        border: "none", borderRadius: "10px",
                                        padding: "10px 24px", fontWeight: 700, fontSize: "14px",
                                        cursor: loading[key] ? "not-allowed" : "pointer",
                                        minWidth: "160px", transition: "all 0.15s",
                                    }}
                                >
                                    {loading[key] ? (
                                        <span>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                                            Fetching…
                                        </span>
                                    ) : (
                                        "⬇ Download Excel"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
